import {
  Dialog,
  Button,
  Typography,
  Box,
  TextField,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import React, { useEffect, useMemo, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { CreateInboundShipmentPlanDialogStyle as styles } from "./CreateInboundShipmentPlanDialogStyle";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { pendingShipmentUpdatedFlagState } from "../recoil/atoms/pendingShipmentAtoms";
import { UserListItem } from "./Table/UserListTable";
import { baseSearchOptionsSelector } from "../recoil/selectors/baseSearchOptionsSelector";
import BaseFilterSelect from "./BaseFilter";
import CommonFilterSelect, {
  CommonFilterSelectOption,
} from "./CommonFilterSelect";
import { countryCodeList } from "../constants/countryCode";
import { prefectureList } from "../constants/prefectureName";
import { ResponseGetShippingSourceInformationResponse } from "../services/OpenAPI";

// データ型
export type ShippingSourceInfoItem = {
  countryId: number;
  countryCode: string;
  postalCode: string;
  stateOrProvinceId: number;
  stateOrProvince: string;
  city: string;
  addressLine1: string;
  name: string;
  phoneNumber: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onCreatePlan: (shippingSourceInfo: ShippingSourceInfoItem) => void;
  onRegisterShipppingInfo: (shippingSourceInfo: ShippingSourceInfoItem) => void;
  shippingInformation: ResponseGetShippingSourceInformationResponse | null;
};

const CreateInboundShipmentPlanDialog: React.FC<Props> = ({
  open,
  // userInfo,
  onClose,
  onCreatePlan,
  onRegisterShipppingInfo,
  shippingInformation = null,
}) => {
  const setPendingShipmentUpdateFlag = useSetRecoilState(
    pendingShipmentUpdatedFlagState
  );

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);

  const [lastName, setLastName] = useState("");

  const [countryCode, setCountryCode] = useState<number | null>(null);
  const [postalCode, setPostalCode] = useState("");
  const [stateOrProvinceId, setStateOrProvinceId] = useState<number | null>(
    null
  );
  const [stateOrProvince, setStateOrProvince] = useState("");
  const [city, setCity] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [shippingSourceInfo, setShippingSourceInfo] =
    useState<ShippingSourceInfoItem>();

  const [editUserInfo, setEditUserInfo] = useState<UserListItem>();

  const [selectedBaseId, setSelectedBaseId] = useState<number | null>(null);

  // 拠点データ取得
  const baseSearchOptions = useRecoilValue(baseSearchOptionsSelector);
  const baseOptions = useMemo(() => {
    return (
      baseSearchOptions?.map((base) => ({
        label: `${base.name}`,
        id: base.id!,
      })) ?? []
    );
  }, [baseSearchOptions]);

  const resetFields = () => {
    // if (userInfo) {
    //   setLastName(userInfo.lastName ?? "");
    //   setFirstrName(userInfo.firstName ?? "");
    //   setMailAddress(userInfo.mailAddress ?? "");
    //   setShopName(userInfo.shopName ?? "");
    //   setRoomId(String(userInfo.roomId ?? ""));
    //   setDriverName(userInfo.driverName ?? "");
    //   setMemo(userInfo.memo ?? "");
    //   // エラー対策: 空配列をデフォルトに
    //   const base = (baseSearchOptions ?? []).find(
    //     (b) => b.name === userInfo.base
    //   );
    //   setSelectedBaseId(base?.id ?? null);
    //   setBaseName(userInfo.base ?? "");
    //   setEditUserInfo(userInfo);
    // }
  };

  useEffect(() => {
    if (!shippingInformation) return;

    // 都道府県と国名のラベルを取得
    const matchedPrefecture = prefectureList.find(
      (p) => p.id === shippingInformation.prefectureId
    );
    const matchedCountry = countryCodeList.find(
      (c) => c.id === shippingInformation.countryId
    );

    // 各フィールドをセット
    setCountryCode(shippingInformation.countryId ?? null);
    setPostalCode(shippingInformation.postalCode ?? "");
    setStateOrProvinceId(shippingInformation.prefectureId ?? null);
    setStateOrProvince(matchedPrefecture?.label ?? "");
    setCity(shippingInformation.city ?? "");
    setAddressLine1(shippingInformation.addressLine1 ?? "");
    setName(shippingInformation.name ?? "");
    setPhoneNumber(shippingInformation.phoneNumber ?? "");

    // shippingSourceInfo にまとめて保存
    setShippingSourceInfo({
      countryId: shippingInformation.countryId ?? 0,
      countryCode: matchedCountry?.label ?? "",
      postalCode: shippingInformation.postalCode ?? "",
      stateOrProvinceId: shippingInformation.prefectureId ?? 0,
      stateOrProvince: matchedPrefecture?.label ?? "",
      city: shippingInformation.city ?? "",
      addressLine1: shippingInformation.addressLine1 ?? "",
      name: shippingInformation.name ?? "",
      phoneNumber: shippingInformation.phoneNumber ?? "",
    });
  }, [shippingInformation]);

  // 入力欄データまとめ（拠点除く）
  const inputItems: [
    string,
    string,
    React.Dispatch<React.SetStateAction<string>>,
    keyof ShippingSourceInfoItem
  ][] = [
    ["郵便番号", postalCode, setPostalCode, "postalCode"],
    ["市町村区", city, setCity, "city"],
    ["番地", addressLine1, setAddressLine1, "addressLine1"],
    ["差出人名", name, setName, "name"],
    ["電話番号", phoneNumber, setPhoneNumber, "phoneNumber"],
  ];

  const validateCheck = () => {
    return (
      countryCode === null ||
      postalCode.trim() === "" ||
      stateOrProvinceId === null ||
      city.trim() === "" ||
      addressLine1.trim() === "" ||
      name.trim() === "" ||
      phoneNumber.trim() === ""
    );
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
        <Dialog
          open={open}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") onClose();
          }}
          disableEnforceFocus
          maxWidth={false}
          fullWidth={false}
          PaperProps={{
            sx: {
              display: "flex",
              width: "800px",
              height: "600px",
              maxWidth: "none",
              maxHeight: "none",
              overflowY: "auto",
            },
          }}
        >
          <Box sx={styles.header}>
            <Typography sx={styles.headerText}>納品プラン作成</Typography>
            <Button
              onClick={() => {
                resetFields();
                onClose();
              }}
              sx={styles.closeButton}
            >
              ×
            </Button>
          </Box>

          <Box sx={styles.topInputRow}>
            <Typography sx={styles.label}>国コード：</Typography>
            <CommonFilterSelect
              options={countryCodeList}
              value={countryCode}
              onSelect={(newCountryId) => {
                setCountryCode(newCountryId);
                const country = countryCodeList.find(
                  (c) => c.id === newCountryId
                );
                // setBaseName(country?.label ?? "");
                setShippingSourceInfo((prev) => ({
                  ...prev!,
                  countryCode: country?.label ?? "",
                  countryId: country?.id ?? 0,
                }));
              }}
              sx={{ width: "400px" }}
              size="medium"
              labelFontSize="0.9rem"
            />
          </Box>

          <Box sx={styles.formArea}>
            {inputItems.slice(0, 1).map(([label, value, setter, key]) => (
              <Box key={label} sx={styles.inputRow}>
                <Typography sx={styles.label}>{label}：</Typography>
                <TextField
                  size="medium"
                  value={value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setter(newValue);
                    setShippingSourceInfo((prev) => ({
                      ...prev!,
                      [key]: newValue,
                    }));
                  }}
                  sx={{ width: "400px" }}
                />
              </Box>
            ))}

            <Box sx={styles.inputRow}>
              <Typography sx={styles.label}>都道府県：</Typography>
              <CommonFilterSelect
                options={prefectureList}
                value={stateOrProvinceId}
                onSelect={(newStateOrProvinceId) => {
                  setStateOrProvinceId(newStateOrProvinceId);
                  const stateOrProvince = prefectureList.find(
                    (c) => c.id === newStateOrProvinceId
                  );
                  setStateOrProvince(stateOrProvince?.label ?? "");
                  setShippingSourceInfo((prev) => ({
                    ...prev!,
                    stateOrProvince: stateOrProvince?.label ?? "",
                    stateOrProvinceId: stateOrProvince?.id ?? 0,
                  }));
                }}
                sx={{ width: "400px" }}
                size="medium"
                labelFontSize="0.9rem"
              />
            </Box>

            {inputItems.slice(1).map(([label, value, setter, key]) => (
              <Box key={label} sx={styles.inputRow}>
                <Typography sx={styles.label}>{label}：</Typography>
                <TextField
                  size="medium"
                  value={value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setter(newValue);
                    setShippingSourceInfo((prev) => ({
                      ...prev!,
                      [key]: newValue,
                    }));
                  }}
                  sx={{ width: "400px" }}
                />
              </Box>
            ))}
          </Box>

          <Button
            onClick={() => {
              onRegisterShipppingInfo(shippingSourceInfo!);
            }}
            variant="contained"
            sx={styles.floatingShippingSourceInfoRegisterButton}
            disabled={validateCheck()}
          >
            配送元情報登録
          </Button>

          <Button
            onClick={() => {
              onCreatePlan(shippingSourceInfo!);
            }}
            variant="contained"
            sx={styles.floatingRegisterButton}
            disabled={validateCheck()}
          >
            納品プラン作成
          </Button>

          <Snackbar
            open={toastOpen}
            autoHideDuration={4000}
            onClose={handleCloseToast}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert
              onClose={handleCloseToast}
              severity={toastSeverity}
              sx={{ width: "100%" }}
              elevation={6}
              variant="filled"
            >
              {toastMessage}
            </MuiAlert>
          </Snackbar>
        </Dialog>
      </LocalizationProvider>
    </>
  );
};

export default CreateInboundShipmentPlanDialog;
